import React, { useContext, useState } from "react";
import { Popconfirm, Tag } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import { TagConfig, TagType } from "@Components/groups/TagProperties";
import { hasCapability } from "@Utils";
import { ITag, ITagMinimal } from "@Components/groups/GroupUtils";
import { MainContext } from "@Context/provider";
import { GroupContext, SystemContext, UserContext } from "@Constants";

interface IProps {
    index: number;
    tag: ITag | ITagMinimal;
}

const GroupsListTag: React.FC<IProps> = ({ index, tag }: IProps) => {
    const { setCurrentGroup, setCurrentUser } = useContext(MainContext);
    const [popupVisible, setPopupVisible] = useState<boolean>(false);
    const [tagVisible, setTagVisible] = useState<boolean>(true);
    const config = TagConfig.get(tag.type);

    /**
     * When a tag is removed, look up the API function, pass in the appropriate params and dispatch it.
     */
    const removeTag = () => {
        if (!("context" in tag)) return;
        setTagVisible(false);
        if (tag.type === TagType.Role) {
            tag.deleteAction.apply(null, [[tag.userId], tag.entityId, tag.groupId]);
        } else {
            tag.deleteAction.apply(null, [tag.entityId]);
        }
    };

    /**
     * Update the ui state with the selected tag
     * @param e
     * @param tag
     */

    const selectTag = (e, tag: ITag) => {
        e.stopPropagation();
        switch (tag.context) {
            case SystemContext:
                break;
            case GroupContext:
                setCurrentGroup({ instance: tag.instanceId });
                setCurrentUser(null);
                break;
            case UserContext:
                setCurrentUser({ instance: tag.instanceId });
                break;
        }
    };

    /**
     * Return true if the user has the capability to remove this tag type.
     */
    const allowDelete = () => {
        if (!("enableDelete" in tag)) return false;
        const tagConfig = TagConfig.get(tag.type);
        return hasCapability(tagConfig.deleteCapability, tag.context, tag.instanceId);
    };

    /**
     * Pop confirm delete ok button selected, or triggered automatically if confirm deleted is disabled.
     */
    const confirmDelete = () => {
        setPopupVisible(false);
        removeTag();
    };

    /**
     * Popup confirm delete cancel button selected.
     */
    const cancelDelete = () => {
        setPopupVisible(false);
    };

    /**
     * Manage the tag removal popup visibility.
     * @param visible
     */
    const handleVisibleChange = (visible: boolean) => {
        if (!visible) {
            setPopupVisible(visible);
            return;
        }

        if (!config.deleteConfirm) {
            confirmDelete();
        } else {
            setPopupVisible(visible);
        }
    };

    return (
        <Tag
            key={"groups-list-tag-" + tag.userId + "-" + index}
            className={"groups-list-tag"}
            color={config.color}
            visible={tagVisible}
        >
            {tag.label}
            {allowDelete() && "enableDelete" in tag && tag.enableDelete && (
                <Popconfirm
                    title={config.deleteMessage}
                    visible={popupVisible}
                    onVisibleChange={handleVisibleChange}
                    onConfirm={confirmDelete}
                    onCancel={cancelDelete}
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{ className: "secondary" }}
                >
                    <button className={"no-style"}>
                        <CloseOutlined />
                    </button>
                </Popconfirm>
            )}
        </Tag>
    );
};
export default GroupsListTag;
